import React from 'react';
import { RobotOutlined, StarFilled, WarningOutlined } from '@ant-design/icons';
import { Col, Row, Table, Tag, Tooltip } from 'antd';
import { FullHeightContainerLayout } from 'components';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faEye, faClockRotateLeft, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;


/**
 * DataTagTable Organism - Table view for DataTag with all columns and functionality
 * 
 * @param {Array} filteredTags - Filtered array of data tags
 * @param {Array} selectedRowKeys - Selected row keys for bulk operations
 * @param {Function} setSelectedRowKeys - Function to update selected rows
 * @param {Array} pinnedTags - Array of pinned tag IDs
 * @param {Function} handlePinTag - Function to handle pin/unpin
 * @param {Object} permission - Permission object
 * @param {Function} onEditDataTag - Function to handle edit
 * @param {Function} onViewDataTag - Function to handle view
 * @param {Function} onViewDataTagHistory - Function to handle history view
 * @param {Function} onDeleteDataTag - Function to handle delete
 * @param {Object} sortConfig - Current sort configuration
 * @param {Function} setSortConfig - Function to update sort configuration
 */
const DataTagTable = ({
  filteredTags,
  selectedRowKeys,
  setSelectedRowKeys,
  pinnedTags,
  handlePinTag,
  permission,
  onEditDataTag,
  onViewDataTag,
  onViewDataTagHistory,
  onDeleteDataTag,
  sortConfig,
  setSortConfig,
}) => {
  const tableContainerRef = React.useRef(null);
  const [tableScrollY, setTableScrollY] = React.useState(320);

  React.useEffect(() => {
    const updateScrollHeight = () => {
      if (!tableContainerRef.current) return;

      const containerHeight = tableContainerRef.current.clientHeight;
      // Reserve space for table header and horizontal scrollbar if present.
      const nextScrollY = Math.max(containerHeight - 70, 160);
      setTableScrollY(nextScrollY);
    };

    updateScrollHeight();

    let resizeObserver;
    if (window.ResizeObserver && tableContainerRef.current) {
      resizeObserver = new window.ResizeObserver(updateScrollHeight);
      resizeObserver.observe(tableContainerRef.current);
    }

    window.addEventListener('resize', updateScrollHeight);

    return () => {
      window.removeEventListener('resize', updateScrollHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return (
    <Row style={{ flexDirection: "column", flexGrow: 1, height: "100%", overflow: "hidden" }}>
      <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <FullHeightContainerLayout
          showHeader={false}
          showFooter={false}
          style={{ overflow: "hidden" }}
          contentStyle={{ overflow: "hidden", minHeight: 0 }}
          content={
            <div ref={tableContainerRef} style={{ height: '100%', minHeight: 0 }}>
              <Table
                size="small"
                rowKey="tagId"
                dataSource={filteredTags}
                pagination={false}  
                // pagination={{
                //   position: ['bottomRight'],
                //   showSizeChanger:false,
                //   showTotal: (total) => `Total ${total} items`,
                //   pageSize: 10,
                // }}
                scroll={{ x: 'max-content', y: tableScrollY }}
                // className="container-height-100"
                rowSelection={{
                  selectedRowKeys: selectedRowKeys,
                  onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
                  preserveSelectedRowKeys: true,
                }}
                data-testid={CypressTestIds.DATATAGS_DATATAGLIST_TABLE}
              > 
              {/* Pin/Favorite Column */}
              <Column
                width="3em"
                render={(value, dataTag) => (
                  <Tooltip title={pinnedTags.includes(dataTag.tagId) ? "Remove from Favorites" : "Mark as Favorite"}>
                    <StarFilled 
                      className={`${pinnedTags.includes(dataTag.tagId) ? 'text-yellow-400' : 'text-gray-400 font-extrabold'} hover:text-yellow-500 cursor-pointer text-xl`}
                      onClick={() => handlePinTag(dataTag.tagId)}
                    />
                  </Tooltip>
                )}
              />
              
              {/* AI Generated Column */}
              <Column
                width="5em"
                render={(value, dataTag) => (
                  dataTag.isAIgenerated ? (       
                    <Tooltip title="This tag was generated using AI">
                      <Tag 
                        icon={<RobotOutlined />}  
                        color="purple" 
                        ghost 
                        className="rounded-full px-2 text-sm font-bold" 
                        style={{ borderColor: '#8c52ff', color: '#5c32fa' }}
                      > 
                        AI
                      </Tag>
                    </Tooltip>
                  ) : null
                )}
              />
              
              {/* Action Buttons Column */}
              <Column
                width="6em"
                render={(value, dataTag, index) => (
                  <div className="flex flex-row items-center gap-2">
                    <Tooltip title='Edit Data Classifier Tags'>
                      <FontAwesomeIcon
                        icon={faPencil}
                        className={permission.canEdit ? "cursor-pointer text-blue-500" : "text-gray-400 opacity-45"}
                        onClick={() => permission.canEdit ? onEditDataTag(dataTag.tagId) : null}
                        data-testid={CypressTestIds.DATATAGS_DATATAGLIST_EDIT_BUTTON}
                      />
                    </Tooltip>
                    <Tooltip title='View Data Classifier Tags'>
                      <FontAwesomeIcon
                        icon={faEye}
                        className={permission.canView ? "cursor-pointer text-blue-500" : "text-gray-400 opacity-45"}
                        onClick={() => permission.canView ? onViewDataTag(dataTag.tagId) : null}
                        data-testid={CypressTestIds.DATATAGS_DATATAGLIST_VIEW_BUTTON}
                      />
                    </Tooltip>
                    <Tooltip title='View Data Classifier History'>
                      <FontAwesomeIcon
                        icon={faClockRotateLeft}
                        className={permission.canView ? "cursor-pointer text-blue-500" : "text-gray-400 opacity-45"}
                        onClick={() => permission.canView ? onViewDataTagHistory(dataTag.tagId) : null}
                        data-testid={CypressTestIds.DATATAGS_DATATAGLIST_HISTORY_BUTTON}
                      />
                    </Tooltip>
                  </div>
                )}
              />
              
              {/* Business Area Column */}
              <Column
                width="15%"
                dataIndex="businessAreaName"
                title={
                  <span>
                    Business Area {sortConfig.key === 'businessAreaName' && (sortConfig.direction === 'ascend' ? '↑':'↓' )}
                  </span>
                }
                sorter
                sortOrder={sortConfig.key === 'businessAreaName' ? sortConfig.direction : null}
                onHeaderCell={() => ({
                  onClick: () => setSortConfig({ 
                    key: 'businessAreaName', 
                    direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' 
                  }),
                })}
                ellipsis={{
                  showTitle: false,
                }}
                render={(value) => value === 'Unassigned' ? (
                  <Tooltip title="This tag must be assigned to a business area before use">
                    <span className="flex items-center gap-1 text-red-400">
                      {value}
                      <WarningOutlined className="text-red-500 text-xl" />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip placement="topLeft" title={value}>
                    {value}
                  </Tooltip>
                )}
              />
              
              {/* Tag Name Column */}
              <Column
                width="10%"
                dataIndex="tagName"
                title={
                  <span>
                    Tag Name {sortConfig.key === 'tagName' && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                  </span>
                }
                sorter
                sortOrder={sortConfig.key === 'tagName' ? sortConfig.direction : null}
                onHeaderCell={() => ({
                  onClick: () => setSortConfig({ 
                    key: 'tagName', 
                    direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' 
                  }),
                })}
                ellipsis={{
                  showTitle: false,
                }}
                render={(value) => <Tooltip placement="topLeft" title={value}>{value}</Tooltip>}
              />
              
              {/* Tag Description Column */}
              <Column
                width="12%"
                dataIndex="tagDescription"
                title="Tag Description"
                ellipsis={{
                  showTitle: false,
                }}
                render={(value) => (
                  <Tooltip placement="topLeft" title={value}>
                    <div className="truncate" style={{ maxWidth: '150px' }}>{value}</div>
                  </Tooltip>
                )}
              />
              
              {/* Last Updated Column */}
              <Column
                width="20%"
                dataIndex="updatedDate"
                title={
                  <span>
                    Last Updated {sortConfig.key === 'updatedDate' && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                  </span>
                }
                sorter
                sortOrder={sortConfig.key === 'updatedDate' ? sortConfig.direction : null}
                onHeaderCell={() => ({
                  onClick: () => setSortConfig({ 
                    key: 'updatedDate', 
                    direction: sortConfig.direction === 'ascend' ? 'descend' : 'ascend' 
                  }),
                })}
                ellipsis={{
                  showTitle: false,
                }}
                render={(value) => {
                  const dateValue = moment.utc(value).toDate().toLocaleString();
                  return <Tooltip placement="topLeft" title={dateValue}>{dateValue}</Tooltip>;
                }}
              />
              
              {/* Updated By Column */}
              <Column
                width="18%"
                dataIndex="updatedByEmail"
                title="Updated By"
                ellipsis={{
                  showTitle: false,
                }}
                render={(value) => {
                  if (value) {
                    return <Tooltip placement="topLeft" title={value}>{value}</Tooltip>;
                  } else {
                    return (
                      <Tooltip placement="topLeft" title="Information is being updated">
                        <span style={{ fontStyle: 'italic', color: '#999' }}>Updating...</span>
                      </Tooltip>
                    );
                  }
                }}
              />
              
              {/* Delete Column */}
              <Column
                width="5%"
                render={(value, dataTag, index) => (
                  <Tooltip title='Delete Data Classifier Tags'>
                    <FontAwesomeIcon
                      icon={faTrashCan}
                      className={permission.canDelete ? "cursor-pointer text-blue-500" : "text-gray-400 opacity-45"}
                      onClick={() => permission.canDelete ? onDeleteDataTag(dataTag) : null}
                      data-testid={CypressTestIds.DATATAGS_DATATAGLIST_DELETE_BUTTON}
                    />
                  </Tooltip>
                )}
              />
              </Table>
            </div>
          }
        />
      </Col>
    </Row>
  );
};

export default React.memo(DataTagTable);
